import React, { useState } from 'react'
import { Plus, Edit2, Trash2, Instagram, Facebook, Twitter, Linkedin, CheckCircle, XCircle } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useProfiles, useCreateProfile, useUpdateProfile, useDeleteProfile } from '@/hooks/useSocialMedia'
import type { SocialProfile } from '@/hooks/useSocialMedia'

const platformIcons = {
  INSTAGRAM: Instagram,
  FACEBOOK: Facebook,
  TWITTER: Twitter,
  LINKEDIN: Linkedin
}

export default function ProfileManager() {
  const { data: profiles = [], isLoading } = useProfiles()
  const createProfile = useCreateProfile()
  const updateProfile = useUpdateProfile()
  const deleteProfile = useDeleteProfile()
  
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [editingProfile, setEditingProfile] = useState<SocialProfile | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    platform: 'INSTAGRAM' as const,
    franchise_region: 'HQ' as const,
    account_id: '',
    settings: {
      default_hashtags: [] as string[],
      auto_publish: false,
      optimal_posting_times: ['09:00', '15:00', '19:00'],
      content_guidelines: '',
      approval_required: true
    }
  })

  const handleCreateOrUpdate = async () => {
    if (editingProfile) {
      await updateProfile.mutateAsync({ id: editingProfile.id, ...formData })
    } else {
      await createProfile.mutateAsync(formData)
    }
    setShowCreateDialog(false)
    setEditingProfile(null)
    resetForm()
  }

  const handleEdit = (profile: SocialProfile) => {
    setEditingProfile(profile)
    setFormData({
      name: profile.name,
      platform: profile.platform,
      franchise_region: profile.franchise_region,
      account_id: profile.account_id || '',
      settings: profile.settings || {
        default_hashtags: [],
        auto_publish: false,
        optimal_posting_times: ['09:00', '15:00', '19:00'],
        content_guidelines: '',
        approval_required: true
      }
    })
    setShowCreateDialog(true)
  }

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this profile? This cannot be undone.')) {
      await deleteProfile.mutateAsync(id)
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      platform: 'INSTAGRAM',
      franchise_region: 'HQ',
      account_id: '',
      settings: {
        default_hashtags: [],
        auto_publish: false,
        optimal_posting_times: ['09:00', '15:00', '19:00'],
        content_guidelines: '',
        approval_required: true
      }
    })
  }

  const franchiseRegions = [
    { value: 'HQ', label: 'Headquarters' },
    { value: 'north_york', label: 'North York' },
    { value: 'london', label: 'London' },
    { value: 'toronto', label: 'Toronto' },
    { value: 'hamilton', label: 'Hamilton' },
    { value: 'mississauga', label: 'Mississauga' },
    { value: 'brampton', label: 'Brampton' },
    { value: 'scarborough', label: 'Scarborough' },
    { value: 'general', label: 'General' }
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Social Media Profiles</h2>
          <p className="text-muted-foreground">Manage your connected social media accounts</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Profile
        </Button>
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground">Loading profiles...</p>
        </div>
      ) : profiles.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-muted-foreground mb-4">No social media profiles configured yet</p>
            <Button onClick={() => setShowCreateDialog(true)}>
              Add your first profile
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {profiles.map((profile) => {
            const Icon = platformIcons[profile.platform] || Instagram
            const regionLabel = franchiseRegions.find(r => r.value === profile.franchise_region)?.label || profile.franchise_region
            
            return (
              <Card key={profile.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-muted rounded-lg">
                        <Icon className="h-6 w-6" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{profile.name}</CardTitle>
                        <CardDescription>{regionLabel}</CardDescription>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(profile)}>
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(profile.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Platform</span>
                      <Badge variant="secondary">{profile.platform}</Badge>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Account ID</span>
                      <span className="font-medium">
                        {profile.account_id || 'Not connected'}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Status</span>
                      <div className="flex items-center gap-1">
                        {profile.account_id ? (
                          <>
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            <span className="text-green-600">Connected</span>
                          </>
                        ) : (
                          <>
                            <XCircle className="h-4 w-4 text-muted-foreground" />
                            <span className="text-muted-foreground">Not connected</span>
                          </>
                        )}
                      </div>
                    </div>
                    
                    {profile.settings?.approval_required && (
                      <div className="pt-2 border-t">
                        <Badge variant="outline" className="text-xs">
                          Approval Required
                        </Badge>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingProfile ? 'Edit Profile' : 'Add Social Media Profile'}</DialogTitle>
            <DialogDescription>
              Configure a social media profile for posting
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Profile Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., FixMyBrick Instagram"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="platform">Platform</Label>
                <Select
                  value={formData.platform}
                  onValueChange={(value: any) => setFormData({ ...formData, platform: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="INSTAGRAM">Instagram</SelectItem>
                    <SelectItem value="FACEBOOK">Facebook</SelectItem>
                    <SelectItem value="TWITTER">Twitter</SelectItem>
                    <SelectItem value="LINKEDIN">LinkedIn</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="franchise_region">Franchise Region</Label>
                <Select
                  value={formData.franchise_region}
                  onValueChange={(value: any) => setFormData({ ...formData, franchise_region: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {franchiseRegions.map((region) => (
                      <SelectItem key={region.value} value={region.value}>
                        {region.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="account_id">Account ID (Optional)</Label>
              <Input
                id="account_id"
                value={formData.account_id}
                onChange={(e) => setFormData({ ...formData, account_id: e.target.value })}
                placeholder="External account identifier"
              />
              <p className="text-xs text-muted-foreground">
                This will be set automatically when you connect the account
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => {
                setShowCreateDialog(false)
                setEditingProfile(null)
                resetForm()
              }}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateOrUpdate}
                disabled={!formData.name || createProfile.isPending || updateProfile.isPending}
              >
                {editingProfile ? 'Update' : 'Create'} Profile
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}